import { DispatchWithoutAction, memo, useRef } from 'react'
import css from './gridItem.module.css'
import { IActiveElement, IGridItem, TMovedType, TPosition, IPositionParams } from '../../grid.types'
import DndIcon from '../../../../../assets/icons/drag-handle.svg?react'
import ResizeIcon from '../../../../../assets/icons/resize-handle.svg?react'
import useDragHandlers from './hooks/useDragHandlers'
import useResizeHandlers from './hooks/useResizeHandlers'
import calculatePositionUtils from './calculatePosition.utils'

interface IProps {
  onDrag: (x: number, y: number, key: IGridItem['key']) => void
  onResize: (width: number, height: number, key: IGridItem['key']) => void
  gridItem: IGridItem
  posParams: IPositionParams
  onActiveElementChange: (activeElement: IActiveElement) => void

  forceUpdate: DispatchWithoutAction
}

const GridItem = (props: IProps) => {
  const containerRef = useRef<HTMLDivElement>()
  const dragHandleRef = useRef<HTMLDivElement>()
  const resizeHandleRef = useRef<HTMLDivElement>()

  const { isDragging, draggingPos, prevItemPos } = useDragHandlers(
    containerRef,
    dragHandleRef,
    props.gridItem,
    props.posParams,
    props.onActiveElementChange,
    props.onDrag
  )

  const { isResizing, cornerPos } = useResizeHandlers(
    containerRef,
    resizeHandleRef,
    props.gridItem,
    props.posParams,
    props.onActiveElementChange,
    props.onResize
  )

  const getMovedType = (): { movedType: TMovedType; movedPos: Partial<TPosition> } | null => {
    switch (true) {
      case Boolean(draggingPos):
        return { movedType: 'dragging', movedPos: { top: draggingPos.y, left: draggingPos.x } }
      case Boolean(cornerPos):
        return { movedType: 'resizing', movedPos: { width: cornerPos.x, height: cornerPos.y } }
      default:
        return null
    }
  }

  const calcGridItemPos = () => {
    const movedData = getMovedType()
    const res = calculatePositionUtils.calcGridItemPosition(
      props.posParams,
      props.gridItem.x,
      props.gridItem.y,
      props.gridItem.width,
      props.gridItem.height,
      movedData
    )
    if (!isDragging) {
      prevItemPos.current = { x: res.left, y: res.top }
    }

    return res
  }

  if (!props.posParams.containerWidth) return null

  return (
    <div
      className={css.wrapper}
      ref={containerRef}
      data-active={isResizing || isDragging}
      style={{
        translate: `${calcGridItemPos().left}px ${calcGridItemPos().top}px`,
        width: `${calcGridItemPos().width}px`,
        height: `${calcGridItemPos().height}px`,
      }}
    >
      <div className={css.container}>
        <div ref={dragHandleRef} className={css.dndIcon}>
          <DndIcon color="var(--text-description)" height="32px" width="16px" />
        </div>
        <div className={css.containerBody}>{props.gridItem.children}</div>
        <div ref={resizeHandleRef} className={css.resizeIcon}>
          <ResizeIcon color="var(--text-description)" height="16px" width="16px" />
        </div>
      </div>
    </div>
  )
}

export default memo(GridItem)
